/**
 * 健康日报 / 周报 V2 字段映射
 */
import dayjs from '@/utils/day'

/**
 * @param {Object} res
 * @returns {boolean}
 */
export function isReportOk(res) {
  return !!(res && (res.code === '1000' || res.code === 1000) && res.data)
}

/**
 * 指标是否为无数据（暂无）
 * @param {{ status?: string, displayValue?: string }|null} m
 * @returns {boolean}
 */
export function metricEmpty(m) {
  if (!m) return true
  if (m.status === 'NO_DATA') return true
  const v = m.displayValue
  return v === '暂无' || v == null || v === '' || v === '—'
}

/**
 * @param {{ status?: string }|null} m
 * @returns {boolean}
 */
export function metricWarn(m) {
  if (!m) return false
  return m.status === 'ABNORMAL'
}

/**
 * @param {Object|null} m
 * @param {string} [fallbackLabel]
 * @returns {{ label: string, value: string, warn: boolean, empty: boolean }}
 */
export function metricRow(m, fallbackLabel) {
  return {
    label: (m && m.name) || fallbackLabel || '',
    value: (m && m.displayValue) || '暂无',
    warn: metricWarn(m),
    empty: metricEmpty(m)
  }
}

/**
 * @param {Object|null} m
 * @returns {boolean}
 */
export function hasRealMetric(m) {
  if (!m) return false
  if (m.status === 'NO_DATA') return false
  if (m.value != null) return true
  const v = m.displayValue
  return !!(v && v !== '暂无')
}

/**
 * @param {Array} arr
 * @returns {boolean}
 */
export function hasNumericPoints(arr) {
  return (arr || []).some(function (v) { return v != null })
}

const WARN_HEX = {
  orange: '#ff9800',
  yellow: '#c0ca33',
  blue: '#64b5f6',
  red: '#e53935',
  gold: '#d4a017',
  grayblue: '#90a4ae',
  gray: '#9e9e9e'
}

/**
 * @param {string} color
 * @returns {string}
 */
export function warnHex(color) {
  return WARN_HEX[color] || '#ff9800'
}

/**
 * @param {string} dateStr
 * @returns {string}
 */
export function formatMd(dateStr) {
  if (!dateStr) return ''
  const p = String(dateStr).split('-')
  if (p.length < 3) return dateStr
  return p[1] + '.' + p[2]
}

/**
 * @param {string} dateStr
 * @returns {string}
 */
export function formatCnDate(dateStr) {
  if (!dateStr) return ''
  const p = String(dateStr).split('-')
  if (p.length < 3) return dateStr
  return Number(p[0]) + '年' + Number(p[1]) + '月' + Number(p[2]) + '日'
}

/**
 * @param {string} datetime
 * @returns {string}
 */
export function formatHm(datetime) {
  if (!datetime) return ''
  const t = String(datetime).split(' ')[1] || ''
  return t.slice(0, 5)
}

/**
 * @param {string} status
 * @returns {number|null}
 */
export function statusLevel(status) {
  if (status === '优秀') return 3
  if (status === '良好') return 2
  if (status === '一般') return 1
  return null
}

/**
 * @param {string} status
 * @returns {string}
 */
export function statusColor(status) {
  if (status === '优秀') return '#00bb82'
  if (status === '良好') return '#8cb900'
  if (status === '一般') return '#ff8a80'
  return '#8a9691'
}

/**
 * 按分数映射档位 class（与评分柱图一致：≥80 优秀 / ≥60 良好 / >0 一般）
 * @param {number|string|null|undefined} score
 * @returns {'is-excellent'|'is-good'|'is-fair'|'is-default'}
 */
export function scoreToneClass(score) {
  const value = Number(score)
  if (!Number.isFinite(value) || value <= 0) return 'is-default'
  if (value >= 80) return 'is-excellent'
  if (value >= 60) return 'is-good'
  return 'is-fair'
}

/**
 * @param {string} code
 * @returns {string}
 */
export function organKey(code) {
  const raw = String(code || '').trim()
  const map = {
    HEART: 'heart',
    LIVER: 'liver',
    SPLEEN: 'spleen',
    KIDNEY: 'kidney',
    心: 'heart',
    肝: 'liver',
    脾: 'spleen',
    肾: 'kidney'
  }
  return map[raw] || map[raw.toUpperCase()] || raw.toLowerCase()
}

/**
 * @param {string} occurTime
 * @returns {number}
 */
export function parseHour(occurTime) {
  if (!occurTime) return 12
  const t = String(occurTime).split(' ')[1] || ''
  const parts = t.split(':')
  return Number(parts[0] || 0) + Number(parts[1] || 0) / 60
}

/**
 * @param {Array} arr
 * @param {number} [len]
 * @returns {Array}
 */
export function pad7(arr, len) {
  const n = len || 7
  const out = (arr || []).slice(0, n)
  while (out.length < n) out.push(null)
  return out
}

/**
 * @param {Array} lowArr
 * @param {Array} hiArr
 * @param {number} [len]
 * @returns {(number[]|null)[]}
 */
export function zipRange(lowArr, hiArr, len) {
  const n = len || 7
  const out = []
  for (let i = 0; i < n; i += 1) {
    const lo = lowArr && lowArr[i]
    const hi = hiArr && hiArr[i]
    if (lo == null || hi == null) out.push(null)
    else out.push([Number(lo), Number(hi)])
  }
  return out
}

/**
 * @param {string} weekStart
 * @param {string} occurTime
 * @returns {number}
 */
export function dayIndex(weekStart, occurTime) {
  if (!weekStart || !occurTime) return -1
  const a = new Date(String(weekStart).slice(0, 10).replace(/-/g, '/'))
  const b = new Date(String(occurTime).slice(0, 10).replace(/-/g, '/'))
  const i = Math.round((b.getTime() - a.getTime()) / 86400000)
  return i >= 0 && i < 7 ? i : -1
}

/**
 * @param {Array} items
 * @param {string[]} symptoms
 * @param {string[]} diseases
 * @returns {Array}
 */
export function mapOrganDims(items, symptoms, diseases) {
  const s = (symptoms || []).filter(Boolean).join('') || '暂无'
  const d = (diseases || []).filter(Boolean).join('') || '暂无'
  return (items || []).map((item, idx) => ({
    key: item.code || ('dim-' + idx),
    label: item.name || item.code || '分项',
    score: item.value != null ? item.value : item.displayValue,
    symptoms: s,
    diseases: d
  }))
}

/**
 * @param {Object} route
 * @returns {string|undefined}
 */
export function queryReportDate(route) {
  const d = route && route.query && route.query.reportDate
  return d ? String(d) : undefined
}

/**
 * 日报默认日期：有 query 用 query，否则昨天（东八区）
 * @param {Object} route
 * @returns {string}
 */
export function resolveDailyReportDate(route) {
  const q = queryReportDate(route)
  if (q) return q
  return dayjs.tz().subtract(1, 'day').format('YYYY-MM-DD')
}

/**
 * 周报默认日期：有 query 用 query，否则上周日（落在上周，东八区）
 * @param {Object} route
 * @returns {string}
 */
export function resolveWeeklyReportDate(route) {
  const q = queryReportDate(route)
  if (q) return q
  return dayjs.tz().startOf('isoWeek').subtract(1, 'day').format('YYYY-MM-DD')
}

/**
 * @param {string} text
 * @returns {string}
 */
export function highlightSummary(text) {
  if (!text) return ''
  return String(text)
    .replace(/优秀/g, '<span class="c-ok">优秀</span>')
    .replace(/良好/g, '<span class="c-good">良好</span>')
    .replace(/一般/g, '<span class="c-fair">一般</span>')
    .replace(/血压计/g, '<span class="c-link">血压计</span>')
    .replace(/(\d+)(?=次|天|分)/g, '<span class="c-danger">$1</span>')
}

/**
 * @param {string} text
 * @returns {string[]}
 */
export function splitAdvice(text) {
  if (!text) return []
  return String(text)
    .split(/\n|；|;/)
    .map(function (s) { return s.trim() })
    .filter(Boolean)
}

/**
 * @param {Array} typeCounts
 * @returns {Array}
 */
export function mapWarnTypes(typeCounts) {
  return (typeCounts || []).map(function (t) {
    return {
      name: t.warnTypeName || t.warnType,
      count: t.count || 0,
      color: warnHex(t.color),
      warnType: t.warnType
    }
  })
}

/**
 * 按预警类型堆叠每日次数
 * @param {Object} warn
 * @param {string} weekStart
 * @returns {Array<{ name: string, color: string, data: number[] }>}
 */
export function buildWarnSeries(warn, weekStart) {
  const typeCounts = (warn && warn.typeCounts) || []
  const points = (warn && warn.points) || []
  if (typeCounts.length) {
    return typeCounts.map(function (t) {
      const days = [0, 0, 0, 0, 0, 0, 0]
      points.forEach(function (p) {
        if (p.warnType !== t.warnType) return
        const i = dayIndex(weekStart, p.occurTime)
        if (i >= 0) days[i] += 1
      })
      const allZero = days.every(function (n) { return !n })
      if (allZero && warn.dailyCounts && typeCounts.length === 1) {
        return {
          name: t.warnTypeName || '健康预警',
          color: warnHex(t.color),
          data: pad7(warn.dailyCounts).map(function (v) { return Number(v) || 0 })
        }
      }
      return {
        name: t.warnTypeName || t.warnType,
        color: warnHex(t.color),
        data: days
      }
    })
  }
  if (warn && warn.dailyCounts) {
    return [{
      name: '健康预警',
      color: '#ff9800',
      data: pad7(warn.dailyCounts).map(function (v) { return Number(v) || 0 })
    }]
  }
  return []
}

/**
 * @param {Object} bp
 * @returns {{ diastolic: string, systolic: string }}
 */
export function parseIdealBp(bp) {
  if (!bp) return { diastolic: '—', systolic: '—' }
  const sys = bp.idealSystolic != null ? '<' + bp.idealSystolic + 'mmHg' : '—'
  const dia = bp.idealDiastolic != null ? '<' + bp.idealDiastolic + 'mmHg' : '—'
  return { systolic: sys, diastolic: dia }
}

/**
 * 血压计记录按日聚合为舒张~收缩区间
 * @param {Array} records
 * @param {string} weekStart
 * @returns {(number[]|null)[]}
 */
export function groupMeterRanges(records, weekStart) {
  const ranges = [null, null, null, null, null, null, null]
  ;(records || []).forEach(function (r) {
    const i = dayIndex(weekStart, r.measureTime)
    if (i < 0) return
    const lo = Number(r.diastolic)
    const hi = Number(r.systolic)
    if (!isFinite(lo) || !isFinite(hi)) return
    if (!ranges[i]) ranges[i] = [Math.min(lo, hi), Math.max(lo, hi)]
    else {
      ranges[i][0] = Math.min(ranges[i][0], lo, hi)
      ranges[i][1] = Math.max(ranges[i][1], lo, hi)
    }
  })
  return ranges
}

/**
 * 周报中医日序列：长度 7，无报告日保持 null，不补 0；字段缺失视为无数据
 * @param {Array|null|undefined} arr
 * @returns {(number|null)[]}
 */
export function mapDailyScores(arr) {
  if (!arr || !arr.length) {
    return [null, null, null, null, null, null, null]
  }
  return pad7(arr).map(function (v) {
    return v == null || v === '' ? null : Number(v)
  })
}

/**
 * @param {Object} tcm
 * @returns {{ tabs: Array, organs: Array, active: string, dailyScores: Object }}
 */
export function mapTcm(tcm) {
  const weekSymptoms = (tcm && tcm.commonSymptoms) || []
  const weekDiseases = (tcm && tcm.commonDiseases) || []
  const organs = ((tcm && tcm.organs) || []).map(function (o) {
    const symptoms = (o.commonSymptoms && o.commonSymptoms.length) ? o.commonSymptoms : weekSymptoms
    const diseases = (o.commonDiseases && o.commonDiseases.length) ? o.commonDiseases : weekDiseases
    return {
      key: organKey(o.organCode),
      code: o.organCode,
      label: o.organName,
      statusName: o.statusName || '',
      multiScore: o.multiScore,
      dims: mapOrganDims(o.items, symptoms, diseases)
    }
  })
  return {
    tabs: [
      { key: 'heart', label: '心' },
      { key: 'liver', label: '肝' },
      { key: 'spleen', label: '脾' },
      { key: 'kidney', label: '肾' }
    ],
    organs: organs,
    active: organKey((tcm && tcm.defaultOrgan) || 'HEART') || 'heart',
    dailyScores: {
      heart: mapDailyScores(tcm && tcm.heartDailyScores),
      liver: mapDailyScores(tcm && tcm.liverDailyScores),
      spleen: mapDailyScores(tcm && tcm.spleenDailyScores),
      kidney: mapDailyScores(tcm && tcm.kidneyDailyScores)
    }
  }
}

/**
 * 从脏腑分项中按 code / name 取分值
 * @param {Object|null} organ
 * @param {string[]} keys
 * @returns {number|undefined}
 */
function pickOrganItemScore(organ, keys) {
  const items = (organ && organ.items) || []
  for (let i = 0; i < keys.length; i += 1) {
    const key = String(keys[i] || '').toLowerCase()
    const hit = items.find(function (item) {
      const code = String(item.code || '').toLowerCase().replace(/_/g, '')
      const name = String(item.name || '')
      return code === key || code.indexOf(key) >= 0 || name.indexOf(keys[i]) >= 0
    })
    if (hit && hit.value != null) return Number(hit.value)
  }
  return undefined
}

/**
 * V2 中医结构转旧版「中医体检」卡片数据
 * @param {Object|null} tcm
 * @param {string} [reportDate]
 * @returns {Object|null}
 */
export function mapTcmToExamCard(tcm, reportDate) {
  if (!tcm || !tcm.evaluable) return null
  const organs = tcm.organs || []
  const findOrgan = function (key) {
    return organs.find(function (o) {
      return organKey(o.organCode) === key || String(o.organCode || '').toUpperCase() === key.toUpperCase()
    }) || null
  }
  const heart = findOrgan('heart')
  const liver = findOrgan('liver')
  const spleen = findOrgan('spleen')
  const kidney = findOrgan('kidney')
  const scores = [heart, liver, spleen, kidney]
    .map(function (o) { return o && o.multiScore != null ? Number(o.multiScore) : null })
    .filter(function (v) { return v != null })
  if (!scores.length) return null
  const avg = Math.round(scores.reduce(function (a, b) { return a + b }, 0) / scores.length)
  return {
    score: avg,
    reportTime: reportDate || '',
    createTime: reportDate || '',
    heartMultiScore: heart && heart.multiScore != null ? Number(heart.multiScore) : 0,
    spiritStateScore: pickOrganItemScore(heart, ['spiritStateScore', 'spirit', '精神']),
    cardconScore: pickOrganItemScore(heart, ['cardconScore', 'cardcon', '收缩']),
    hrchaScore: pickOrganItemScore(heart, ['hrchaScore', 'hrcha', '混沌']),
    bpcompScore: pickOrganItemScore(heart, ['bpcompScore', 'bpcomp', '顺应']),
    liverMultiScore: liver && liver.multiScore != null ? Number(liver.multiScore) : 0,
    sleepQualityScore: pickOrganItemScore(liver, ['sleepQualityScore', 'sleep', '睡眠']),
    diurnalRhythmScore: pickOrganItemScore(liver, ['diurnalRhythmScore', 'diurnal', '节律']),
    synScore: pickOrganItemScore(liver, ['synScore', 'syn', '交感']),
    pynScore: pickOrganItemScore(liver, ['pynScore', 'pyn', '副交感']),
    autoners: pickOrganItemScore(liver, ['autoners', '情绪']),
    gzsbScore: pickOrganItemScore(liver, ['gzsbScore', 'gzsb', '脑血管']),
    spleenMultiScore: spleen && spleen.multiScore != null ? Number(spleen.multiScore) : 0,
    pzsdeScore: pickOrganItemScore(spleen, ['pzsdeScore', 'pzsde', '黏度']),
    kidneyMultiScore: kidney && kidney.multiScore != null ? Number(kidney.multiScore) : 0,
    energyScore: pickOrganItemScore(kidney, ['energyScore', 'energy', '活力']),
    bmiScore: pickOrganItemScore(kidney, ['bmiScore', 'bmi', '形之']),
    qiScore: pickOrganItemScore(kidney, ['qiScore', 'qi', '气之'])
  }
}



// WEBPACK FOOTER //
// ./src/utils/healthReportV2.js